import { useSearchParams } from 'react-router';
import { IndexedFilters } from '../types/filters';
import { fullstorySubmitFilterEvent } from '@/fullstory/utils/fullstory-submit-filter-event';
import { useMemo, useState, useEffect, useCallback } from 'react';
import {
  convertSearchParamsToFilters,
  convertFiltersToSearchParams,
} from '../utils/convert-filters';
import isStringArray from '@/common/utils/is-string-array';
import getInventoryFilterColors from '../utils/get-inventory-filter-colors';
import isColorFilter from '../utils/is-color-filter';
import {
  isTypeIndexedNestedFilter,
  isTypeNumberRange,
} from '../utils/filter-menu-type-guards';

// Filter categories that can be represented by range filter pills and should only count as 1 applied filter but have an array of values
const RANGE_FILTERS = ['seats', 'model_year'];

interface InventoryFiltersProps {
  filterOptions: IndexedFilters;
}

export interface InventoryFiltersResponse {
  selectedFilters: IndexedFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
  handleClearAll: () => void;
  hasNoFilters: boolean;
  filterCount: number;
}

export default function useInventoryFilters({
  filterOptions,
}: InventoryFiltersProps): InventoryFiltersResponse {
  const [searchParams, setSearchParams] = useSearchParams();

  // Convert search params to filters
  const activeFilters = useMemo(
    () =>
      convertSearchParamsToFilters({
        searchParams,
        availableFilters: filterOptions,
      }),
    [filterOptions, searchParams]
  );

  // Initialize selected filters from active filters
  const [selectedFilters, setSelectedFilters] = useState(activeFilters);

  useEffect(() => {
    const newSearchParams = convertFiltersToSearchParams({
      searchParams,
      selectedFilters,
    });

    // Avoid infinite loop by comparing current searchParams to derived filters. Only update the URL if searchParams don't already match the desired state
    if (newSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(newSearchParams, { preventScrollReset: true });
      fullstorySubmitFilterEvent(selectedFilters);
      sessionStorage?.setItem('inventoryFilters', newSearchParams.toString());
    }
  }, [filterOptions, searchParams, selectedFilters, setSearchParams]);

  const handleClearAll = useCallback(() => {
    setSelectedFilters({});
    setSearchParams(new URLSearchParams(), { preventScrollReset: true });
    sessionStorage?.removeItem('inventoryFilters');
  }, [setSearchParams]);

  const hasNoFilters = useMemo(() => {
    return Object.keys(selectedFilters).length === 0;
  }, [selectedFilters]);

  const filterCount = useMemo(
    () =>
      Object.entries(selectedFilters).reduce((acc, [key, filter]) => {
        /**
         * If the filter is an array, then add the number of filters in that array to the accumulator.
         * Otherwise, add the number of sub filters to the accumulator and the number of main filters.
         * e.g. { Toyota: ["Corolla", "4Runner"]} counts as 3 filters. 1 for the make filter of Toyota
         * and 2 for the model filters under the Toyota make.
         */
        if (isTypeIndexedNestedFilter(filter)) {
          return (
            acc +
            Object.keys(filter).length +
            Object.entries(filter).reduce(
              (acc, [, subFilter]) => acc + subFilter.length,
              0
            )
          );
        } else {
          /**
           * For range filters, only count as 1 filter
           * For color filters, only count the colors bucketed under "Others" as 1 filter. All other colors are counted individually.
           * For all other array filters, count the number of filters in the array
           */
          if (RANGE_FILTERS.includes(key) || isTypeNumberRange(filter)) {
            return acc + 1;
          } else if (isColorFilter(key) && isStringArray(filter)) {
            const displayedColors = getInventoryFilterColors(filter);
            return (
              // Add the extra 1 if there are colors under "Others" that have been applied
              acc +
              displayedColors.length +
              (filter.length === displayedColors.length ? 0 : 1)
            );
          } else {
            return acc + filter.length;
          }
        }
      }, 0),
    [selectedFilters]
  );

  return {
    selectedFilters,
    setSelectedFilters,
    handleClearAll,
    hasNoFilters,
    filterCount,
  };
}
